import { useDebounce } from '@uidotdev/usehooks';
import { cn } from '../cn';
import { useScrollControl } from '../Modal';
import { Button } from '../Button';
import { IconClose } from '../Icons';
import { SideMenuListSeparator } from './components/SideMenuListSeparator';
import { OverlayContainer } from '@react-aria/overlays';

type SideMenuProps = React.PropsWithChildren<{
  show: boolean,
  toggle: () => void,
  logout: () => void,
  lightModeImage: string,
  darkModeImage: string,
  isLoggedIn: boolean,
  firstName?: string,
  email?: string,
  loginTo?: string,
  loginOnPress?: () => void,
  customHeader?: React.ReactNode,
  width?: 'md'|'lg',
}>;

const widthMap = {
  md: 'sm:w-[328px]',
  lg: 'sm:w-[375px]',
};

export const SideMenu = ({ children, customHeader, show, toggle, loginTo = '/login', loginOnPress, logout, lightModeImage, darkModeImage, isLoggedIn, firstName, email, width = 'md' }: SideMenuProps) => {
  const debouncedShow = useDebounce(show, 100);
  useScrollControl(show);

  return (<OverlayContainer>
    <div
      className={cn(
        'fixed top-0 bottom-0 left-0 right-0 z-40 bg-neutral-300/50 pointer-events-none opacity-0 transition-all',
        show && 'opacity-100',
        debouncedShow && 'pointer-events-auto',
      )} onClick={toggle}
    />
    <div className={cn(
      `flex flex-col fixed top-0 right-0 h-full w-screen ${widthMap[width]} bg-surface dark:bg-surface-dark z-[9000] transform translate-x-[110%] transition-transform duration-300 overflow-y-scroll`,
      show && 'translate-x-0',
    )} style={{ boxShadow: '-4px 0px 6px 0px rgba(0, 0, 0, 0.1)' }}>
      <div className='absolute top-5 right-4 flex justify-end gap-2'>
        <Button unstyled onPress={toggle} className='w-9 h-9 rounded-full justify-center items-center flex' excludeFromTabOrder={!show}>
          <IconClose/>
        </Button>
      </div>
      <div className='py-[9px] pt-6 px-3 gap-1 flex flex-col items-start'>
        <img src={lightModeImage} className='block sm:hidden h-16 mb-6 dark:hidden' aria-label='Rock Island Auction Company Logo'/>
        <img src={darkModeImage} alt='Rock Island Auction Company' className='h-16 mb-6 hidden dark:block dark:sm:hidden' aria-label='Rock Island Auction Company Logo'/>
        {customHeader && customHeader ||<>
          {isLoggedIn && <>
            <div className='font-bold text-lg leading-6 tracking-wide'>Hello, {firstName}.</div>
            <div className='font-normal text-text-light text-sm leading-5 tracking-wide'>{email}</div>
          </> || <>
            <div className='font-bold text-lg leading-6 tracking-wide'>Hello.</div>
            <div className='text-sm leading-5 tracking-wide'>
              Please <Button variant='link' className='p-0 no-underline hover:underline' to={loginTo} onPress={() => {
                if(loginOnPress) {
                  loginOnPress();
                } else if(toggle) {
                  toggle();
                }
              }} excludeFromTabOrder={!show}>Log In</Button>
              {', or '}
              <Button variant='link' className='p-0 no-underline hover:underline' onPress={() => window.location.pathname = '/create-account'} tabIndex={!show ? -1 : 0}>Create an account</Button>.
            </div>
          </>}
        </>}
      </div>
      <SideMenuListSeparator/>
      {children}
    </div>
  </OverlayContainer>);
};
